import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { appState } from "@/app/appState";
import { syncCheckout } from "@/api";

export const BillingReturnPage = observer(() => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [attempts, setAttempts] = useState(0);
    const [phase, setPhase] = useState<"confirming" | "activated" | "processing">("confirming");
    const maxAttempts = 15;
    const queryOrgId = Number(searchParams.get("org"));
    // The organisation that was just paid for; it need not be the active one.
    const orgId = queryOrgId > 0 ? queryOrgId : appState.user?.active_organisation?.id;
    const settingsPath = orgId ? `/workspace/settings/org/${orgId}` : "/workspace/settings";

    useEffect(() => {
        if (!orgId) {
            navigate(settingsPath);
            return;
        }

        let pollAttempts = 0;
        let timer: ReturnType<typeof setTimeout>;

        // Sync asks Mollie directly, so this does not depend on the webhook.
        const poll = async () => {
            pollAttempts++;
            try {
                const res = await syncCheckout({ body: { id: orgId } });
                const data = res.data as any;

                if (data?.billing_status === "active") {
                    setPhase("activated");
                    timer = setTimeout(() => navigate(settingsPath), 1500);
                    return;
                }
            } catch (_) { }

            if (pollAttempts < maxAttempts) {
                setAttempts(pollAttempts);
                timer = setTimeout(poll, 2000);
            } else {
                setPhase("processing");
                timer = setTimeout(() => navigate(settingsPath), 4000);
            }
        };

        timer = setTimeout(poll, 1500);
        return () => clearTimeout(timer);
    }, [orgId]);

    const message =
        phase === "activated"
            ? appState.t("billing.return.activated")
            : phase === "processing"
                ? appState.t("billing.return.processing")
                : attempts > 0
                    ? appState.t("billing.return.confirmingProgress", { attempts, max: maxAttempts })
                    : appState.t("billing.return.confirming");

    return (
        <div className="flex flex-col items-center justify-center min-h-screen gap-4">
            <div className="hsub">{message}</div>
            <button
                className="link-muted"
                onClick={() => navigate(settingsPath)}
            >
                {appState.t("billing.return.goSettings")}
            </button>
        </div>
    );
});
